// @flow
import * as React from 'react';

import { CircleAlert } from 'lucide-react';
import punycode from 'punycode.js';
import { Controller, useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { Icon, FormControl, Radio, RadioGroup, Text, View } from 'reshaped';

import type { ChangeHandler } from 'reshaped';

import { closeNotification, notifyDanger } from '@gandi/notifications';
import { Button } from '@gandi/reshaped';
import { useTranslator } from '@gandi/translate';

import { useQuote } from '../../hooks/useQuote';

import { FileUploadField } from './FileUploadField';
import { TextAreaField } from './TextAreaField';

import styles from './DomainsList.module.css';

/**
 * Converts a raw domains string into a unique, Punycode-encoded API payload array.
 * @param domainsString Raw input string from textarea (supports spaces, commas, or semicolons).
 * @returns Array of unique, ASCII-compatible domain strings.
 */
export const formatDomainsPayload = (domainsString: string): Array<string> => {
  const headerRegex = /^fqdn[^\r\n.]*/im;
  const cleanedString = domainsString.replace(headerRegex, '');

  const domains = cleanedString
    .split(/[\s,;]+/)
    .filter(Boolean)
    .map(punycode.toASCII);
  return [...new Set(domains)];
};

export type Props = {
  domainsConfirmed: boolean,
  setDomainsConfirmed: React.ReactSetStateFunction<boolean>,
};

const NOTIFICATION_ID = 'acme-domains-quote-error';
export function DomainsList({
  domainsConfirmed,
  setDomainsConfirmed,
}: Props): React.MixedElement | null {
  const [domainsType, setDomainsType] = React.useState('manually');

  const dispatch = useDispatch();
  const { __ } = useTranslator();

  const form = useFormContext();
  const { isValid, isSubmitted, isSubmitting } = form.formState;

  const provider: string = form.watch('provider');
  const validation_type: string = form.watch('validation_type');
  const domains: string = form.watch('domains');

  const { handlePost, isPosting, options, pricingParams } = useQuote();

  const [isPending, startTransition] = React.useTransition();

  React.useEffect(() => {
    if (!domains) return;
    setDomainsConfirmed(prev => {
      if (prev) return false;
      return prev;
    });
  }, [domains, setDomainsConfirmed]);

  const handleChangeRadio: ChangeHandler<string> = ({ value }) => {
    if (value) {
      startTransition(() => {
        setDomainsType(value);
        if (value === 'import') form.resetField('domains');
      });
    }
  };

  const handleConfirm = async (evt: SyntheticEvent<>) => {
    evt.preventDefault();
    if (domains.length === 0) return;

    const apiSearch = formatDomainsPayload(domains);
    try {
      await handlePost({
        domains: apiSearch,
        ...pricingParams,
        provider,
        validation_type,
      });

      startTransition(() => {
        setDomainsConfirmed(true);
        // $FlowFixMe[incompatible-call]
        dispatch(closeNotification(NOTIFICATION_ID));
      });
    } catch (err) {
      const apiErrors = err?.errors?.domains;

      if (Array.isArray(apiErrors)) {
        const message = apiErrors.filter(Boolean);

        form.setError('domains', { message });
        // $FlowFixMe[incompatible-call]
        dispatch(closeNotification(NOTIFICATION_ID));
      } else {
        dispatch(
          notifyDanger({
            id: NOTIFICATION_ID,
            message:
              err._error ||
              __(
                'An error has occurred while fetching prices. Please try again.',
              ),
          }),
        );
      }
    }
  };

  return (
    <View
      padding={7}
      borderRadius="large"
      borderColor="neutral"
      gap={{ s: 12, m: 6 }}
    >
      <FormControl group>
        <FormControl.Label>
          <Text
            as="h2"
            variant="body-2"
            weight="bold"
            attributes={{ style: { marginBottom: 'var(--rs-unit-x6)' } }}
          >
            {__('List of affected domains')}
          </Text>
        </FormControl.Label>

        <RadioGroup
          name="domains-type"
          onChange={handleChangeRadio}
          defaultValue="manually"
        >
          <View
            className={styles.layout}
            attributes={{
              style: {
                '--mobile-order': domainsType === 'manually' ? 2 : 4,
              },
            }}
          >
            {/* $FlowFixMe[incompatible-type] */}
            <Radio value="manually">
              <Text variant="body-2">{__('Add domain names manually')}</Text>
            </Radio>
            {/* $FlowFixMe[incompatible-type] */}
            <Radio value="import">
              <Text variant="body-2">
                {__('Import domain names from a CSV file')}
              </Text>
            </Radio>

            <View className={styles.content}>
              <Controller
                name="domains"
                control={form.control}
                rules={{ required: __('This field is required') }}
                render={({ field, fieldState }) => {
                  const errorMessage = fieldState.error?.message;
                  let errors: Array<string> = errorMessage
                    ? Array.isArray(errorMessage)
                      ? errorMessage
                      : [errorMessage]
                    : [];

                  if (
                    !errors.length &&
                    domainsConfirmed &&
                    !isPending &&
                    !isSubmitting &&
                    !options?.length
                  ) {
                    errors.push(
                      __(
                        'An error has occurred while fetching prices. Please try again.',
                      ),
                    );
                  }

                  if (!isSubmitted) {
                    errors = errors.filter(
                      err => err !== __('This field is required'),
                    );
                  }

                  return (
                    <>
                      {domainsType === 'manually' && (
                        <TextAreaField field={field} />
                      )}
                      {domainsType === 'import' && (
                        <FileUploadField field={field} />
                      )}

                      {!!errors.length && (
                        <View gap={2} as="ul">
                          {errors.map(err => (
                            <View
                              direction="row"
                              gap={2}
                              align="center"
                              as="li"
                              key={err}
                            >
                              <Icon
                                svg={CircleAlert}
                                size={4}
                                color="critical"
                              />
                              <Text color="critical" variant="body-3">
                                {err}
                              </Text>
                            </View>
                          ))}
                        </View>
                      )}
                    </>
                  );
                }}
              />
            </View>
          </View>
        </RadioGroup>
      </FormControl>

      {(!domainsConfirmed || isPending) && (
        <Button
          color="primary"
          size="large"
          attributes={{ style: { alignSelf: 'end' } }}
          disabled={!domains?.length || !isValid}
          loading={isPosting || isPending}
          onClick={handleConfirm}
          type="submit"
        >
          {__('Confirm')}
        </Button>
      )}
    </View>
  );
}
